import { reactive } from 'vue'
import { PIPE_EVENTS, type PipeInterface } from './usePipe'

/** Payload of `PIPE_EVENTS.variableTick`. */
export interface VariableTickPayload {
  /** Interpolation key, prefix included — e.g. `var.global.fasterGoalDate`. */
  key: string
  value: unknown
  /** How long the frame stays on screen, and how long its flash runs. */
  durationMs: number
}

export interface VariableTickFrame {
  value: unknown
  durationMs: number
  /** Bumped on every frame, so a consumer can restart a CSS animation. */
  seq: number
}

export interface VariableTickState {
  frames: Record<string, VariableTickFrame>
}

const RELEASE_GRACE_MS = 100

const statePerPipe = new WeakMap<PipeInterface, VariableTickState>()

/**
 * Transient display overrides for interpolated variables, fed by
 * `PIPE_EVENTS.variableTick`. One subscription and one reactive map per pipe
 * instance, shared by every caller.
 *
 * @param pipe the injected pipe instance
 * @returns the pipe's tick state — `frames[key]` while a frame is on screen
 */
export function useVariableTicks(pipe: PipeInterface): VariableTickState {
  const existing = statePerPipe.get(pipe)
  if (existing) return existing

  const state = reactive<VariableTickState>({ frames: {} })
  const timers = new Map<string, ReturnType<typeof setTimeout>>()
  let seq = 0

  statePerPipe.set(pipe, state)

  pipe.subscribe(PIPE_EVENTS.variableTick, (payload: VariableTickPayload) => {
    const key = payload?.key
    const durationMs = Number(payload?.durationMs)
    if (!key || !Number.isFinite(durationMs) || durationMs <= 0) return

    const pending = timers.get(key)
    if (pending) clearTimeout(pending)

    const frame: VariableTickFrame = { value: payload.value, durationMs, seq: ++seq }
    state.frames[key] = frame

    timers.set(
      key,
      setTimeout(() => {
        timers.delete(key)
        if (state.frames[key]?.seq === frame.seq) delete state.frames[key]
      }, durationMs + RELEASE_GRACE_MS),
    )
  })

  return state
}
